import _inherits from "/app/node_modules/@babel/runtime/helpers/esm/inherits.js";
import _createSuper from "/app/node_modules/@babel/runtime/helpers/esm/createSuper.js";
import _slicedToArray from "/app/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import _createForOfIteratorHelper from "/app/node_modules/@babel/runtime/helpers/esm/createForOfIteratorHelper.js";
import _createClass from "/app/node_modules/@babel/runtime/helpers/esm/createClass.js";
import _classCallCheck from "/app/node_modules/@babel/runtime/helpers/esm/classCallCheck.js";
/**
The default maximum length of a `TreeBuffer` node.
*/
var DefaultBufferLength = 1024;
var nextPropID = 0;
var Range = /*#__PURE__*/_createClass(function Range(from, to) {
  _classCallCheck(this, Range);
  this.from = from;
  this.to = to;
});
/**
Each [node type](#common.NodeType) or [individual tree](#common.Tree)
can have metadata associated with it in props. Instances of this
class represent prop names.
*/
var NodeProp = /*#__PURE__*/function () {
  /**
  Create a new node prop type.
  */
  function NodeProp() {
    var config = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    _classCallCheck(this, NodeProp);
    this.id = nextPropID++;
    this.perNode = !!config.perNode;
    this.deserialize = config.deserialize || function () {
      throw new Error("This node type doesn't define a deserialize function");
    };
  }
  /**
  This is meant to be used with
  [`NodeSet.extend`](#common.NodeSet.extend) or
  [`LRParser.configure`](#lr.ParserConfig.props) to compute
  prop values for each node type in the set. Takes a [match
  object](#common.NodeType^match) or function that returns undefined
  if the node type doesn't get this prop, and the prop's value if
  it does.
  */
  _createClass(NodeProp, [{
    key: "add",
    value: function add(match) {
      var _this = this;
      if (this.perNode) throw new RangeError("Can't add per-node props to node types");
      if (typeof match != "function") match = NodeType.match(match);
      return function (type) {
        var result = match(type);
        return result === undefined ? null : [_this, result];
      };
    }
  }]);
  return NodeProp;
}();
/**
Prop that is used to describe matching delimiters. For opening
delimiters, this holds an array of node names (written as a
space-separated string when declaring this prop in a grammar)
for the node types of closing delimiters that match it.
*/
NodeProp.closedBy = new NodeProp({
  deserialize: function deserialize(str) {
    return str.split(" ");
  }
});
/**
The inverse of [`closedBy`](#common.NodeProp^closedBy). This is
attached to closing delimiters, holding an array of node names
of types of matching opening delimiters.
*/
NodeProp.openedBy = new NodeProp({
  deserialize: function deserialize(str) {
    return str.split(" ");
  }
});
/**
Used to assign node types to groups (for example, all node
types that represent an expression could be tagged with an
`"Expression"` group).
*/
NodeProp.group = new NodeProp({
  deserialize: function deserialize(str) {
    return str.split(" ");
  }
});
/**
The hash of the [context](#lr.ContextTracker.constructor)
that the node was parsed in, if any. Used to limit reuse of
contextual nodes.
*/
NodeProp.contextHash = new NodeProp({
  perNode: true
});
/**
The distance beyond the end of the node that the tokenizer
looked ahead for any of the tokens inside the node. (The LR
parser only stores this when it is larger than 25, for
efficiency reasons.)
*/
NodeProp.lookAhead = new NodeProp({
  perNode: true
});
/**
This per-node prop is used to replace a given node, or part of a
node, with another tree. This is useful to include trees from
different languages in mixed-language parsers.
*/
NodeProp.mounted = new NodeProp({
  perNode: true
});
/**
A mounted tree, which can be [stored](#common.NodeProp^mounted) on
a tree node to indicate that parts of its content are
represented by another tree.
*/
var MountedTree = /*#__PURE__*/function () {
  function MountedTree(
  /**
  The inner tree.
  */
  tree,
  /**
  If this is null, this tree replaces the entire node (it will
  be included in the regular iteration instead of its host
  node). If not, only the given ranges are considered to be
  covered by this tree. This is used for trees that are mixed in
  a way that isn't strictly hierarchical. Such mounted trees are
  only entered by [`resolveInner`](#common.Tree.resolveInner)
  and [`enter`](#common.SyntaxNode.enter).
  */
  overlay,
  /**
  The parser used to create this subtree.
  */
  parser) {
    _classCallCheck(this, MountedTree);
    this.tree = tree;
    this.overlay = overlay;
    this.parser = parser;
  }
  /**
  @internal
  */
  _createClass(MountedTree, null, [{
    key: "get",
    value: function get(tree) {
      return tree && tree.props && tree.props[NodeProp.mounted.id];
    }
  }]);
  return MountedTree;
}();
var noProps = Object.create(null);
/**
Each node in a syntax tree has a node type associated with it.
*/
var NodeType = /*#__PURE__*/function () {
  /**
  @internal
  */
  function NodeType(
  /**
  The name of the node type. Not necessarily unique, but if the
  grammar was written properly, different node types with the
  same name within a node set should play the same semantic
  role.
  */
  name,
  /**
  @internal
  */
  props,
  /**
  The id of this node in its set. Corresponds to the term ids
  used in the parser.
  */
  id) {
    var flags = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : 0;
    _classCallCheck(this, NodeType);
    this.name = name;
    this.props = props;
    this.id = id;
    this.flags = flags;
  }
  /**
  Define a node type.
  */
  _createClass(NodeType, [{
    key: "prop",
    value:
    /**
    Retrieves a node prop for this type. Will return `undefined` if
    the prop isn't present on this node.
    */
    function prop(_prop) {
      return this.props[_prop.id];
    }
    /**
    True when this is the top node of a grammar.
    */
  }, {
    key: "isTop",
    get: function get() {
      return (this.flags & 1 /* NodeFlag.Top */) > 0;
    }
    /**
    True when this node is produced by a skip rule.
    */
  }, {
    key: "isSkipped",
    get: function get() {
      return (this.flags & 2 /* NodeFlag.Skipped */) > 0;
    }
    /**
    Indicates whether this is an error node.
    */
  }, {
    key: "isError",
    get: function get() {
      return (this.flags & 4 /* NodeFlag.Error */) > 0;
    }
    /**
    When true, this node type doesn't correspond to a user-declared
    named node, for example because it is used to cache repetition.
    */
  }, {
    key: "isAnonymous",
    get: function get() {
      return (this.flags & 8 /* NodeFlag.Anonymous */) > 0;
    }
    /**
    Returns true when this node's name or one of its
    [groups](#common.NodeProp^group) matches the given string.
    */
  }, {
    key: "is",
    value: function is(name) {
      if (typeof name == 'string') {
        if (this.name == name) return true;
        var group = this.prop(NodeProp.group);
        return group ? group.indexOf(name) > -1 : false;
      }
      return this.id == name;
    }
    /**
    Create a function from node types to arbitrary values by
    specifying an object whose property names are node or
    [group](#common.NodeProp^group) names. Often useful with
    [`NodeProp.add`](#common.NodeProp.add). You can put multiple
    names, separated by spaces, in a single property name to map
    multiple node names to a single value.
    */
  }], [{
    key: "define",
    value: function define(spec) {
      var props = spec.props && spec.props.length ? Object.create(null) : noProps;
      var flags = (spec.top ? 1 /* NodeFlag.Top */ : 0) | (spec.skipped ? 2 /* NodeFlag.Skipped */ : 0) | (spec.error ? 4 /* NodeFlag.Error */ : 0) | (spec.name == null ? 8 /* NodeFlag.Anonymous */ : 0);
      var type = new NodeType(spec.name || "", props, spec.id, flags);
      if (spec.props) {
        var _iterator = _createForOfIteratorHelper(spec.props),
          _step;
        try {
          for (_iterator.s(); !(_step = _iterator.n()).done;) {
            var src = _step.value;
            if (!Array.isArray(src)) src = src(type);
            if (src) {
              if (src[0].perNode) throw new RangeError("Can't store a per-node prop on a node type");
              props[src[0].id] = src[1];
            }
          }
        } catch (err) {
          _iterator.e(err);
        } finally {
          _iterator.f();
        }
      }
      return type;
    }
  }, {
    key: "match",
    value: function match(map) {
      var direct = Object.create(null);
      for (var prop in map) {
        var _iterator2 = _createForOfIteratorHelper(prop.split(" ")),
          _step2;
        try {
          for (_iterator2.s(); !(_step2 = _iterator2.n()).done;) {
            var name = _step2.value;
            direct[name] = map[prop];
          }
        } catch (err) {
          _iterator2.e(err);
        } finally {
          _iterator2.f();
        }
      }
      return function (node) {
        for (var groups = node.prop(NodeProp.group), i = -1; i < (groups ? groups.length : 0); i++) {
          var found = direct[i < 0 ? node.name : groups[i]];
          if (found) return found;
        }
      };
    }
  }]);
  return NodeType;
}();
/**
An empty dummy node type to use when no actual type is available.
*/
NodeType.none = new NodeType("", Object.create(null), 0, 8 /* NodeFlag.Anonymous */);
/**
A node set holds a collection of node types. It is used to
compactly represent trees by storing their type ids, rather than a
full pointer to the type object, in a numeric array. Each parser
[has](#lr.LRParser.nodeSet) a node set, and [tree
buffers](#common.TreeBuffer) can only store collections of nodes
from the same set. A set can have a maximum of 2**16 (65536) node
types in it, so that the ids fit into 16-bit typed array slots.
*/
var NodeSet = /*#__PURE__*/function () {
  /**
  Create a set with the given types. The `id` property of each
  type should correspond to its position within the array.
  */
  function NodeSet(
  /**
  The node types in this set, by id.
  */
  types) {
    _classCallCheck(this, NodeSet);
    this.types = types;
    for (var i = 0; i < types.length; i++) if (types[i].id != i) throw new RangeError("Node type ids should correspond to array positions when creating a node set");
  }
  /**
  Create a copy of this set with some node properties added. The
  arguments to this method can be created with
  [`NodeProp.add`](#common.NodeProp.add).
  */
  _createClass(NodeSet, [{
    key: "extend",
    value: function extend() {
      var newTypes = [];
      for (var _len = arguments.length, props = new Array(_len), _key = 0; _key < _len; _key++) {
        props[_key] = arguments[_key];
      }
      var _iterator3 = _createForOfIteratorHelper(this.types),
        _step3;
      try {
        for (_iterator3.s(); !(_step3 = _iterator3.n()).done;) {
          var type = _step3.value;
          var newProps = null;
          var _iterator4 = _createForOfIteratorHelper(props),
            _step4;
          try {
            for (_iterator4.s(); !(_step4 = _iterator4.n()).done;) {
              var source = _step4.value;
              var add = source(type);
              if (add) {
                if (!newProps) newProps = Object.assign({}, type.props);
                newProps[add[0].id] = add[1];
              }
            }
          } catch (err) {
            _iterator4.e(err);
          } finally {
            _iterator4.f();
          }
          newTypes.push(newProps ? new NodeType(type.name, newProps, type.id, type.flags) : type);
        }
      } catch (err) {
        _iterator3.e(err);
      } finally {
        _iterator3.f();
      }
      return new NodeSet(newTypes);
    }
  }]);
  return NodeSet;
}();
var CachedNode = new WeakMap(),
  CachedInnerNode = new WeakMap();
/**
Options that control iteration. Can be combined with the `|`
operator to enable multiple ones.
*/
var IterMode;
(function (IterMode) {
  /**
  When enabled, iteration will only visit [`Tree`](#common.Tree)
  objects, not nodes packed into
  [`TreeBuffer`](#common.TreeBuffer)s.
  */
  IterMode[IterMode["ExcludeBuffers"] = 1] = "ExcludeBuffers";
  /**
  Enable this to make iteration include anonymous nodes (such as
  the nodes that wrap repeated grammar constructs into a balanced
  tree).
  */
  IterMode[IterMode["IncludeAnonymous"] = 2] = "IncludeAnonymous";
  /**
  By default, regular [mounted](#common.NodeProp^mounted) nodes
  replace their base node in iteration. Enable this to ignore them
  instead.
  */
  IterMode[IterMode["IgnoreMounts"] = 4] = "IgnoreMounts";
  /**
  This option only applies in
  [`enter`](#common.SyntaxNode.enter)-style methods. It tells the
  library to not enter mounted overlays if one covers the given
  position.
  */
  IterMode[IterMode["IgnoreOverlays"] = 8] = "IgnoreOverlays";
})(IterMode || (IterMode = {}));
/**
A piece of syntax tree. There are two ways to approach these
trees: the way they are actually stored in memory, and the
convenient way.

Syntax trees are stored as a tree of `Tree` and `TreeBuffer`
objects. By packing detail information into `TreeBuffer` leaf
nodes, the representation is made a lot more memory-efficient.

However, when you want to actually work with tree nodes, this
representation is very awkward, so most client code will want to
use the [`TreeCursor`](#common.TreeCursor) or
[`SyntaxNode`](#common.SyntaxNode) interface instead, which provides
a view on some part of this data structure, and can be used to
move around to adjacent nodes.
*/
var Tree = /*#__PURE__*/function () {
  /**
  Construct a new tree. See also [`Tree.build`](#common.Tree^build).
  */
  function Tree(
  /**
  The type of the top node.
  */
  type,
  /**
  This node's child nodes.
  */
  children,
  /**
  The positions (offsets relative to the start of this tree) of
  the children.
  */
  positions,
  /**
  The total length of this tree
  */
  length,
  /**
  Per-node [node props](#common.NodeProp) to associate with this node.
  */
  props) {
    _classCallCheck(this, Tree);
    this.type = type;
    this.children = children;
    this.positions = positions;
    this.length = length;
    /**
    @internal
    */
    this.props = null;
    if (props && props.length) {
      this.props = Object.create(null);
      var _iterator5 = _createForOfIteratorHelper(props),
        _step5;
      try {
        for (_iterator5.s(); !(_step5 = _iterator5.n()).done;) {
          var _step5$value = _slicedToArray(_step5.value, 2),
            prop = _step5$value[0],
            value = _step5$value[1];
          this.props[typeof prop == "number" ? prop : prop.id] = value;
        }
      } catch (err) {
        _iterator5.e(err);
      } finally {
        _iterator5.f();
      }
    }
  }
  /**
  @internal
  */
  _createClass(Tree, [{
    key: "toString",
    value: function toString() {
      var mounted = MountedTree.get(this);
      if (mounted && !mounted.overlay) return mounted.tree.toString();
      var children = "";
      var _iterator6 = _createForOfIteratorHelper(this.children),
        _step6;
      try {
        for (_iterator6.s(); !(_step6 = _iterator6.n()).done;) {
          var ch = _step6.value;
          var str = ch.toString();
          if (str) {
            if (children) children += ",";
            children += str;
          }
        }
      } catch (err) {
        _iterator6.e(err);
      } finally {
        _iterator6.f();
      }
      return !this.type.name ? children : (/\W/.test(this.type.name) && !this.type.isError ? JSON.stringify(this.type.name) : this.type.name) + (children.length ? "(" + children + ")" : "");
    }
    /**
    Get a [tree cursor](#common.TreeCursor) positioned at the top of
    the tree. Mode can be used to [control](#common.IterMode) which
    nodes the cursor visits.
    */
  }, {
    key: "cursor",
    value: function cursor() {
      var mode = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;
      return new TreeCursor(this.topNode, mode);
    }
    /**
    Get a [tree cursor](#common.TreeCursor) pointing into this tree
    at the given position and side (see
    [`moveTo`](#common.TreeCursor.moveTo).
    */
  }, {
    key: "cursorAt",
    value: function cursorAt(pos) {
      var side = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
      var mode = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 0;
      var scope = CachedNode.get(this) || this.topNode;
      var cursor = new TreeCursor(scope);
      cursor.moveTo(pos, side);
      CachedNode.set(this, cursor._tree);
      return cursor;
    }
    /**
    Get a [syntax node](#common.SyntaxNode) object for the top of the
    tree.
    */
  }, {
    key: "topNode",
    get: function get() {
      return new TreeNode(this, 0, 0, null);
    }
    /**
    Get the [syntax node](#common.SyntaxNode) at the given position.
    If `side` is -1, this will move into nodes that end at the
    position. If 1, it'll move into nodes that start at the
    position. With 0, it'll only enter nodes that cover the position
    from both sides.
    
    Note that this will not enter
    [overlays](#common.MountedTree.overlay), and you often want
    [`resolveInner`](#common.Tree.resolveInner) instead.
    */
  }, {
    key: "resolve",
    value: function resolve(pos) {
      var side = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
      var node = resolveNode(CachedNode.get(this) || this.topNode, pos, side, false);
      CachedNode.set(this, node);
      return node;
    }
    /**
    Like [`resolve`](#common.Tree.resolve), but will enter
    [overlaid](#common.MountedTree.overlay) nodes, producing a syntax node
    pointing into the innermost overlaid tree at the given position
    (with parent links going through all parent structure, including
    the host trees).
    */
  }, {
    key: "resolveInner",
    value: function resolveInner(pos) {
      var side = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
      var node = resolveNode(CachedInnerNode.get(this) || this.topNode, pos, side, true);
      CachedInnerNode.set(this, node);
      return node;
    }
    /**
    In some situations, it can be useful to iterate through all
    nodes around a position, including those in overlays that don't
    directly cover the position. This method gives you an iterator
    that will produce all nodes, from small to big, around the given
    position.
    */
  }, {
    key: "resolveStack",
    value: function resolveStack(pos) {
      var side = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
      return stackIterator(this, pos, side);
    }
    /**
    Iterate over the tree and its children, calling `enter` for any
    node that touches the `from`/`to` region (if given) before
    running over such a node's children, and `leave` (if given) when
    leaving the node. When `enter` returns `false`, that node will
    not have its children iterated over (or `leave` called).
    */
  }, {
    key: "iterate",
    value: function iterate(spec) {
      var enter = spec.enter,
        leave = spec.leave,
        _spec$from = spec.from,
        from = _spec$from === void 0 ? 0 : _spec$from,
        _spec$to = spec.to,
        to = _spec$to === void 0 ? this.length : _spec$to;
      var mode = spec.mode || 0,
        anon = (mode & IterMode.IncludeAnonymous) > 0;
      for (var c = this.cursor(mode | IterMode.IncludeAnonymous);;) {
        var entered = false;
        if (c.from <= to && c.to >= from && (!anon && c.type.isAnonymous || enter(c) !== false)) {
          if (c.firstChild()) continue;
          entered = true;
        }
        for (;;) {
          if (entered && leave && (anon || !c.type.isAnonymous)) leave(c);
          if (c.nextSibling()) break;
          if (!c.parent()) return;
          entered = true;
        }
      }
    }
    /**
    Get the value of the given [node prop](#common.NodeProp) for this
    node. Works with both per-node and per-type props.
    */
  }, {
    key: "prop",
    value: function prop(_prop2) {
      return !_prop2.perNode ? this.type.prop(_prop2) : this.props ? this.props[_prop2.id] : undefined;
    }
    /**
    Returns the node's [per-node props](#common.NodeProp.perNode) in a
    format that can be passed to the [`Tree`](#common.Tree)
    constructor.
    */
  }, {
    key: "propValues",
    get: function get() {
      var result = [];
      if (this.props) for (var id in this.props) result.push([+id, this.props[id]]);
      return result;
    }
    /**
    Balance the direct children of this tree, producing a copy of
    which may have children grouped into subtrees with type
    [`NodeType.none`](#common.NodeType^none).
    */
  }, {
    key: "balance",
    value: function balance() {
      var _this2 = this;
      var config = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
      return this.children.length <= 8 /* Balance.BranchFactor */ ? this : balanceRange(NodeType.none, this.children, this.positions, 0, this.children.length, 0, this.length, function (children, positions, length) {
        return new Tree(_this2.type, children, positions, length, _this2.propValues);
      }, config.makeTree || function (children, positions, length) {
        return new Tree(NodeType.none, children, positions, length);
      });
    }
    /**
    Build a tree from a postfix-ordered buffer of node information,
    or a cursor over such a buffer.
    */
  }], [{
    key: "build",
    value: function build(data) {
      return buildTree(data);
    }
  }]);
  return Tree;
}();
/**
The empty tree
*/
Tree.empty = new Tree(NodeType.none, [], [], 0);
var FlatBufferCursor = /*#__PURE__*/function () {
  function FlatBufferCursor(buffer, index) {
    _classCallCheck(this, FlatBufferCursor);
    this.buffer = buffer;
    this.index = index;
  }
  _createClass(FlatBufferCursor, [{
    key: "id",
    get: function get() {
      return this.buffer[this.index - 4];
    }
  }, {
    key: "start",
    get: function get() {
      return this.buffer[this.index - 3];
    }
  }, {
    key: "end",
    get: function get() {
      return this.buffer[this.index - 2];
    }
  }, {
    key: "size",
    get: function get() {
      return this.buffer[this.index - 1];
    }
  }, {
    key: "pos",
    get: function get() {
      return this.index;
    }
  }, {
    key: "next",
    value: function next() {
      this.index -= 4;
    }
  }, {
    key: "fork",
    value: function fork() {
      return new FlatBufferCursor(this.buffer, this.index);
    }
  }]);
  return FlatBufferCursor;
}();
/**
Tree buffers contain (type, start, end, endIndex) quads for each
node. In such a buffer, nodes are stored in prefix order (parents
before children, with the endIndex of the parent indicating which
children belong to it).
*/
var TreeBuffer = /*#__PURE__*/function () {
  /**
  Create a tree buffer.
  */
  function TreeBuffer(
  /**
  The buffer's content.
  */
  buffer,
  /**
  The total length of the group of nodes in the buffer.
  */
  length,
  /**
  The node set used in this buffer.
  */
  set) {
    _classCallCheck(this, TreeBuffer);
    this.buffer = buffer;
    this.length = length;
    this.set = set;
  }
  /**
  @internal
  */
  _createClass(TreeBuffer, [{
    key: "type",
    get: function get() {
      return NodeType.none;
    }
    /**
    @internal
    */
  }, {
    key: "toString",
    value: function toString() {
      var result = [];
      for (var index = 0; index < this.buffer.length;) {
        result.push(this.childString(index));
        index = this.buffer[index + 3];
      }
      return result.join(",");
    }
    /**
    @internal
    */
  }, {
    key: "childString",
    value: function childString(index) {
      var id = this.buffer[index],
        endIndex = this.buffer[index + 3];
      var type = this.set.types[id],
        result = type.name;
      if (/\W/.test(result) && !type.isError) result = JSON.stringify(result);
      index += 4;
      if (endIndex == index) return result;
      var children = [];
      while (index < endIndex) {
        children.push(this.childString(index));
        index = this.buffer[index + 3];
      }
      return result + "(" + children.join(",") + ")";
    }
    /**
    @internal
    */
  }, {
    key: "findChild",
    value: function findChild(startIndex, endIndex, dir, pos, side) {
      var buffer = this.buffer,
        pick = -1;
      for (var i = startIndex; i != endIndex; i = buffer[i + 3]) {
        if (checkSide(side, pos, buffer[i + 1], buffer[i + 2])) {
          pick = i;
          if (dir > 0) break;
        }
      }
      return pick;
    }
    /**
    @internal
    */
  }, {
    key: "slice",
    value: function slice(startI, endI, from) {
      var b = this.buffer;
      var copy = new Uint16Array(endI - startI),
        len = 0;
      for (var i = startI, j = 0; i < endI;) {
        copy[j++] = b[i++];
        copy[j++] = b[i++] - from;
        var to = copy[j++] = b[i++] - from;
        copy[j++] = b[i++] - startI;
        len = Math.max(len, to);
      }
      return new TreeBuffer(copy, len, this.set);
    }
  }]);
  return TreeBuffer;
}();
function checkSide(side, pos, from, to) {
  switch (side) {
    case -2 /* Side.Before */:
      return from < pos;
    case -1 /* Side.AtOrBefore */:
      return to >= pos && from < pos;
    case 0 /* Side.Around */:
      return from < pos && to > pos;
    case 1 /* Side.AtOrAfter */:
      return from <= pos && to > pos;
    case 2 /* Side.After */:
      return to > pos;
    case 4 /* Side.DontCare */:
      return true;
  }
}
function resolveNode(node, pos, side, overlays) {
  var _a;
  // Move up to a node that actually holds the position, if possible
  while (node.from == node.to || (side < 1 ? node.from >= pos : node.from > pos) || (side > -1 ? node.to <= pos : node.to < pos)) {
    var parent = !overlays && node instanceof TreeNode && node.index < 0 ? null : node.parent;
    if (!parent) return node;
    node = parent;
  }
  var mode = overlays ? 0 : IterMode.IgnoreOverlays;
  // Must go up out of overlays when those do not overlap with pos
  if (overlays) for (var scan = node, _parent2 = scan.parent; _parent2; scan = _parent2, _parent2 = scan.parent) {
    if (scan instanceof TreeNode && scan.index < 0 && ((_a = _parent2.enter(pos, side, mode)) === null || _a === void 0 ? void 0 : _a.from) != scan.from) node = _parent2;
  }
  for (;;) {
    var inner = node.enter(pos, side, mode);
    if (!inner) return node;
    node = inner;
  }
}
var BaseNode = /*#__PURE__*/function () {
  function BaseNode() {
    _classCallCheck(this, BaseNode);
  }
  _createClass(BaseNode, [{
    key: "cursor",
    value: function cursor() {
      var mode = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;
      return new TreeCursor(this, mode);
    }
  }, {
    key: "getChild",
    value: function getChild(type) {
      var before = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
      var after = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : null;
      var r = _getChildren(this, type, before, after);
      return r.length ? r[0] : null;
    }
  }, {
    key: "getChildren",
    value: function getChildren(type) {
      var before = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
      var after = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : null;
      return _getChildren(this, type, before, after);
    }
  }, {
    key: "resolve",
    value: function resolve(pos) {
      var side = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
      return resolveNode(this, pos, side, false);
    }
  }, {
    key: "resolveInner",
    value: function resolveInner(pos) {
      var side = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
      return resolveNode(this, pos, side, true);
    }
  }, {
    key: "matchContext",
    value: function matchContext(context) {
      return matchNodeContext(this, context);
    }
  }, {
    key: "enterUnfinishedNodesBefore",
    value: function enterUnfinishedNodesBefore(pos) {
      var scan = this.childBefore(pos),
        node = this;
      while (scan) {
        var last = scan.lastChild;
        if (!last || last.to != scan.to) break;
        if (last.type.isError && last.from == last.to) {
          node = scan;
          scan = last.prevSibling;
        } else {
          scan = last;
        }
      }
      return node;
    }
  }, {
    key: "node",
    get: function get() {
      return this;
    }
  }, {
    key: "next",
    get: function get() {
      return this.parent;
    }
  }]);
  return BaseNode;
}();
var TreeNode = /*#__PURE__*/function (_BaseNode) {
  _inherits(TreeNode, _BaseNode);
  var _super = _createSuper(TreeNode);
  function TreeNode(_tree, from,
  // Index in parent node, set to -1 if the node is not a direct child of _parent.node (overlay)
  index, _parent) {
    var _this3;
    _classCallCheck(this, TreeNode);
    _this3 = _super.call(this);
    _this3._tree = _tree;
    _this3.from = from;
    _this3.index = index;
    _this3._parent = _parent;
    return _this3;
  }
  _createClass(TreeNode, [{
    key: "type",
    get: function get() {
      return this._tree.type;
    }
  }, {
    key: "name",
    get: function get() {
      return this._tree.type.name;
    }
  }, {
    key: "to",
    get: function get() {
      return this.from + this._tree.length;
    }
  }, {
    key: "nextChild",
    value: function nextChild(i, dir, pos, side) {
      var mode = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : 0;
      for (var parent = this;;) {
        for (var _parent$_tree = parent._tree, children = _parent$_tree.children, positions = _parent$_tree.positions, e = dir > 0 ? children.length : -1; i != e; i += dir) {
          var next = children[i],
            start = positions[i] + parent.from;
          if (!checkSide(side, pos, start, start + next.length)) continue;
          if (next instanceof TreeBuffer) {
            if (mode & IterMode.ExcludeBuffers) continue;
            var index = next.findChild(0, next.buffer.length, dir, pos - start, side);
            if (index > -1) return new BufferNode(new BufferContext(parent, next, i, start), null, index);
          } else if (mode & IterMode.IncludeAnonymous || !next.type.isAnonymous || hasChild(next)) {
            var mounted = void 0;
            if (!(mode & IterMode.IgnoreMounts) && (mounted = MountedTree.get(next)) && !mounted.overlay) return new TreeNode(mounted.tree, start, i, parent);
            var inner = new TreeNode(next, start, i, parent);
            return mode & IterMode.IncludeAnonymous || !inner.type.isAnonymous ? inner : inner.nextChild(dir < 0 ? next.children.length - 1 : 0, dir, pos, side);
          }
        }
        if (mode & IterMode.IncludeAnonymous || !parent.type.isAnonymous) return null;
        if (parent.index >= 0) i = parent.index + dir;else i = dir < 0 ? -1 : parent._parent._tree.children.length;
        parent = parent._parent;
        if (!parent) return null;
      }
    }
  }, {
    key: "firstChild",
    get: function get() {
      return this.nextChild(0, 1, 0, 4 /* Side.DontCare */);
    }
  }, {
    key: "lastChild",
    get: function get() {
      return this.nextChild(this._tree.children.length - 1, -1, 0, 4 /* Side.DontCare */);
    }
  }, {
    key: "childAfter",
    value: function childAfter(pos) {
      return this.nextChild(0, 1, pos, 2 /* Side.After */);
    }
  }, {
    key: "childBefore",
    value: function childBefore(pos) {
      return this.nextChild(this._tree.children.length - 1, -1, pos, -2 /* Side.Before */);
    }
  }, {
    key: "enter",
    value: function enter(pos, side) {
      var mode = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 0;
      var mounted;
      if (!(mode & IterMode.IgnoreOverlays) && (mounted = MountedTree.get(this._tree)) && mounted.overlay) {
        var rPos = pos - this.from;
        var _iterator7 = _createForOfIteratorHelper(mounted.overlay),
          _step7;
        try {
          for (_iterator7.s(); !(_step7 = _iterator7.n()).done;) {
            var _step7$value = _step7.value,
              from = _step7$value.from,
              to = _step7$value.to;
            if ((side > 0 ? from <= rPos : from < rPos) && (side < 0 ? to >= rPos : to > rPos)) return new TreeNode(mounted.tree, mounted.overlay[0].from + this.from, -1, this);
          }
        } catch (err) {
          _iterator7.e(err);
        } finally {
          _iterator7.f();
        }
      }
      return this.nextChild(0, 1, pos, side, mode);
    }
  }, {
    key: "nextSignificantParent",
    value: function nextSignificantParent() {
      var val = this;
      while (val.type.isAnonymous && val._parent) val = val._parent;
      return val;
    }
  }, {
    key: "parent",
    get: function get() {
      return this._parent ? this._parent.nextSignificantParent() : null;
    }
  }, {
    key: "nextSibling",
    get: function get() {
      return this._parent && this.index >= 0 ? this._parent.nextChild(this.index + 1, 1, 0, 4 /* Side.DontCare */) : null;
    }
  }, {
    key: "prevSibling",
    get: function get() {
      return this._parent && this.index >= 0 ? this._parent.nextChild(this.index - 1, -1, 0, 4 /* Side.DontCare */) : null;
    }
  }, {
    key: "tree",
    get: function get() {
      return this._tree;
    }
  }, {
    key: "toTree",
    value: function toTree() {
      return this._tree;
    }
    /**
    @internal
    */
  }, {
    key: "toString",
    value: function toString() {
      return this._tree.toString();
    }
  }]);
  return TreeNode;
}(BaseNode);
function _getChildren(node, type, before, after) {
  var cur = node.cursor(),
    result = [];
  if (!cur.firstChild()) return result;
  if (before != null) while (!cur.type.is(before)) if (!cur.nextSibling()) return result;
  for (;;) {
    if (after != null && cur.type.is(after)) return result;
    if (cur.type.is(type)) result.push(cur.node);
    if (!cur.nextSibling()) return after == null ? result : [];
  }
}
function matchNodeContext(node, context) {
  var i = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : context.length - 1;
  for (var p = node.parent; i >= 0; p = p.parent) {
    if (!p) return false;
    if (!p.type.isAnonymous) {
      if (context[i] && context[i] != p.name) return false;
      i--;
    }
  }
  return true;
}
var BufferContext = /*#__PURE__*/_createClass(function BufferContext(parent, buffer, index, start) {
  _classCallCheck(this, BufferContext);
  this.parent = parent;
  this.buffer = buffer;
  this.index = index;
  this.start = start;
});
var BufferNode = /*#__PURE__*/function (_BaseNode2) {
  _inherits(BufferNode, _BaseNode2);
  var _super2 = _createSuper(BufferNode);
  function BufferNode(context, _parent, index) {
    var _this4;
    _classCallCheck(this, BufferNode);
    _this4 = _super2.call(this);
    _this4.context = context;
    _this4._parent = _parent;
    _this4.index = index;
    _this4.type = context.buffer.set.types[context.buffer.buffer[index]];
    return _this4;
  }
  _createClass(BufferNode, [{
    key: "name",
    get: function get() {
      return this.type.name;
    }
  }, {
    key: "from",
    get: function get() {
      return this.context.start + this.context.buffer.buffer[this.index + 1];
    }
  }, {
    key: "to",
    get: function get() {
      return this.context.start + this.context.buffer.buffer[this.index + 2];
    }
  }, {
    key: "child",
    value: function child(dir, pos, side) {
      var buffer = this.context.buffer;
      var index = buffer.findChild(this.index + 4, buffer.buffer[this.index + 3], dir, pos - this.context.start, side);
      return index < 0 ? null : new BufferNode(this.context, this, index);
    }
  }, {
    key: "firstChild",
    get: function get() {
      return this.child(1, 0, 4 /* Side.DontCare */);
    }
  }, {
    key: "lastChild",
    get: function get() {
      return this.child(-1, 0, 4 /* Side.DontCare */);
    }
  }, {
    key: "childAfter",
    value: function childAfter(pos) {
      return this.child(1, pos, 2 /* Side.After */);
    }
  }, {
    key: "childBefore",
    value: function childBefore(pos) {
      return this.child(-1, pos, -2 /* Side.Before */);
    }
  }, {
    key: "enter",
    value: function enter(pos, side) {
      var mode = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 0;
      if (mode & IterMode.ExcludeBuffers) return null;
      var buffer = this.context.buffer;
      var index = buffer.findChild(this.index + 4, buffer.buffer[this.index + 3], side > 0 ? 1 : -1, pos - this.context.start, side);
      return index < 0 ? null : new BufferNode(this.context, this, index);
    }
  }, {
    key: "parent",
    get: function get() {
      return this._parent || this.context.parent.nextSignificantParent();
    }
  }, {
    key: "externalSibling",
    value: function externalSibling(dir) {
      return this._parent ? null : this.context.parent.nextChild(this.context.index + dir, dir, 0, 4 /* Side.DontCare */);
    }
  }, {
    key: "nextSibling",
    get: function get() {
      var buffer = this.context.buffer;
      var after = buffer.buffer[this.index + 3];
      if (after < (this._parent ? buffer.buffer[this._parent.index + 3] : buffer.buffer.length)) return new BufferNode(this.context, this._parent, after);
      return this.externalSibling(1);
    }
  }, {
    key: "prevSibling",
    get: function get() {
      var buffer = this.context.buffer;
      var parentStart = this._parent ? this._parent.index + 4 : 0;
      if (this.index == parentStart) return this.externalSibling(-1);
      return new BufferNode(this.context, this._parent, buffer.findChild(parentStart, this.index, -1, 0, 4 /* Side.DontCare */));
    }
  }, {
    key: "tree",
    get: function get() {
      return null;
    }
  }, {
    key: "toTree",
    value: function toTree() {
      var children = [],
        positions = [];
      var buffer = this.context.buffer;
      var startI = this.index + 4,
        endI = buffer.buffer[this.index + 3];
      if (endI > startI) {
        var from = buffer.buffer[this.index + 1];
        children.push(buffer.slice(startI, endI, from));
        positions.push(0);
      }
      return new Tree(this.type, children, positions, this.to - this.from);
    }
    /**
    @internal
    */
  }, {
    key: "toString",
    value: function toString() {
      return this.context.buffer.childString(this.index);
    }
  }]);
  return BufferNode;
}(BaseNode);
function iterStack(heads) {
  if (!heads.length) return null;
  if (heads.length == 1) return heads[0];
  var pick = 0,
    picked = heads[0];
  for (var i = 1; i < heads.length; i++) {
    var node = heads[i];
    if (node.from > picked.from || node.to < picked.to) {
      picked = node;
      pick = i;
    }
  }
  var next = picked instanceof TreeNode && picked.index < 0 ? null : picked.parent;
  var newHeads = heads.slice();
  if (next) newHeads[pick] = next;else newHeads.splice(pick, 1);
  return new StackIterator(newHeads, picked);
}
var StackIterator = /*#__PURE__*/function () {
  function StackIterator(heads, node) {
    _classCallCheck(this, StackIterator);
    this.heads = heads;
    this.node = node;
  }
  _createClass(StackIterator, [{
    key: "next",
    get: function get() {
      return iterStack(this.heads);
    }
  }]);
  return StackIterator;
}();
function stackIterator(tree, pos, side) {
  var inner = tree.resolveInner(pos, side),
    layers = null;
  for (var scan = inner instanceof TreeNode ? inner : inner.context.parent; scan; scan = scan.parent) {
    if (scan.index < 0) {
      // This is an overlay root
      var parent = scan.parent;
      (layers || (layers = [inner])).push(parent.resolve(pos, side));
      scan = parent;
    } else {
      var mount = MountedTree.get(scan.tree);
      // Relevant overlay branching off
      if (mount && mount.overlay && mount.overlay[0].from <= pos && mount.overlay[mount.overlay.length - 1].to >= pos) {
        var root = new TreeNode(mount.tree, mount.overlay[0].from + scan.from, 0, null);
        (layers || (layers = [inner])).push(resolveNode(root, pos, side, false));
      }
    }
  }
  return layers ? iterStack(layers) : inner;
}
/**
A tree cursor object focuses on a given node in a syntax tree, and
allows you to move to adjacent nodes.
*/
var TreeCursor = /*#__PURE__*/function () {
  /**
  @internal
  */
  function TreeCursor(node) {
    var mode = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
    _classCallCheck(this, TreeCursor);
    this.mode = mode;
    /**
    @internal
    */
    this.buffer = null;
    this.stack = [];
    /**
    @internal
    */
    this.index = 0;
    this.bufferNode = null;
    if (node instanceof TreeNode) {
      this.yieldNode(node);
    } else {
      this._tree = node.context.parent;
      this.buffer = node.context;
      for (var n = node._parent; n; n = n._parent) this.stack.unshift(n.index);
      this.bufferNode = node;
      this.yieldBuf(node.index);
    }
  }
  _createClass(TreeCursor, [{
    key: "name",
    get:
    /**
    Shorthand for `.type.name`.
    */
    function get() {
      return this.type.name;
    }
  }, {
    key: "yieldNode",
    value: function yieldNode(node) {
      if (!node) return false;
      this._tree = node;
      this.type = node.type;
      this.from = node.from;
      this.to = node.to;
      return true;
    }
  }, {
    key: "yieldBuf",
    value: function yieldBuf(index, type) {
      this.index = index;
      var _this$buffer = this.buffer,
        start = _this$buffer.start,
        buffer = _this$buffer.buffer;
      this.type = type || buffer.set.types[buffer.buffer[index]];
      this.from = start + buffer.buffer[index + 1];
      this.to = start + buffer.buffer[index + 2];
      return true;
    }
  }, {
    key: "yield",
    value: function _yield(node) {
      if (!node) return false;
      if (node instanceof TreeNode) {
        this.buffer = null;
        return this.yieldNode(node);
      }
      this.buffer = node.context;
      return this.yieldBuf(node.index, node.type);
    }
    /**
    @internal
    */
  }, {
    key: "toString",
    value: function toString() {
      return this.buffer ? this.buffer.buffer.childString(this.index) : this._tree.toString();
    }
    /**
    @internal
    */
  }, {
    key: "enterChild",
    value: function enterChild(dir, pos, side) {
      if (!this.buffer) return this["yield"](this._tree.nextChild(dir < 0 ? this._tree._tree.children.length - 1 : 0, dir, pos, side, this.mode));
      var buffer = this.buffer.buffer;
      var index = buffer.findChild(this.index + 4, buffer.buffer[this.index + 3], dir, pos - this.buffer.start, side);
      if (index < 0) return false;
      this.stack.push(this.index);
      return this.yieldBuf(index);
    }
    /**
    Move the cursor to this node's first child. When this returns
    false, the node has no child, and the cursor has not been moved.
    */
  }, {
    key: "firstChild",
    value: function firstChild() {
      return this.enterChild(1, 0, 4 /* Side.DontCare */);
    }
    /**
    Move the cursor to this node's last child.
    */
  }, {
    key: "lastChild",
    value: function lastChild() {
      return this.enterChild(-1, 0, 4 /* Side.DontCare */);
    }
    /**
    Move the cursor to the first child that ends after `pos`.
    */
  }, {
    key: "childAfter",
    value: function childAfter(pos) {
      return this.enterChild(1, pos, 2 /* Side.After */);
    }
    /**
    Move to the last child that starts before `pos`.
    */
  }, {
    key: "childBefore",
    value: function childBefore(pos) {
      return this.enterChild(-1, pos, -2 /* Side.Before */);
    }
    /**
    Move the cursor to the child around `pos`. If side is -1 the
    child may end at that position, when 1 it may start there. This
    will also enter [overlaid](#common.MountedTree.overlay)
    [mounted](#common.NodeProp^mounted) trees unless `overlays` is
    set to false.
    */
  }, {
    key: "enter",
    value: function enter(pos, side) {
      var mode = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : this.mode;
      if (!this.buffer) return this["yield"](this._tree.enter(pos, side, mode));
      return mode & IterMode.ExcludeBuffers ? false : this.enterChild(1, pos, side);
    }
    /**
    Move to the node's parent node, if this isn't the top node.
    */
  }, {
    key: "parent",
    value: function parent() {
      if (!this.buffer) return this.yieldNode(this.mode & IterMode.IncludeAnonymous ? this._tree._parent : this._tree.parent);
      if (this.stack.length) return this.yieldBuf(this.stack.pop());
      var parent = this.mode & IterMode.IncludeAnonymous ? this.buffer.parent : this.buffer.parent.nextSignificantParent();
      this.buffer = null;
      return this.yieldNode(parent);
    }
    /**
    @internal
    */
  }, {
    key: "sibling",
    value: function sibling(dir) {
      if (!this.buffer) return !this._tree._parent ? false : this["yield"](this._tree.index < 0 ? null : this._tree._parent.nextChild(this._tree.index + dir, dir, 0, 4 /* Side.DontCare */, this.mode));
      var buffer = this.buffer.buffer,
        d = this.stack.length - 1;
      if (dir < 0) {
        var parentStart = d < 0 ? 0 : this.stack[d] + 4;
        if (this.index != parentStart) return this.yieldBuf(buffer.findChild(parentStart, this.index, -1, 0, 4 /* Side.DontCare */));
      } else {
        var after = buffer.buffer[this.index + 3];
        if (after < (d < 0 ? buffer.buffer.length : buffer.buffer[this.stack[d] + 3])) return this.yieldBuf(after);
      }
      return d < 0 ? this["yield"](this.buffer.parent.nextChild(this.buffer.index + dir, dir, 0, 4 /* Side.DontCare */, this.mode)) : false;
    }
    /**
    Move to this node's next sibling, if any.
    */
  }, {
    key: "nextSibling",
    value: function nextSibling() {
      return this.sibling(1);
    }
    /**
    Move to this node's previous sibling, if any.
    */
  }, {
    key: "prevSibling",
    value: function prevSibling() {
      return this.sibling(-1);
    }
  }, {
    key: "atLastNode",
    value: function atLastNode(dir) {
      var index,
        parent,
        buffer = this.buffer;
      if (buffer) {
        if (dir > 0) {
          if (this.index < buffer.buffer.buffer.length) return false;
        } else {
          for (var i = 0; i < this.index; i++) if (buffer.buffer.buffer[i + 3] < this.index) return false;
        }
        index = buffer.index;
        parent = buffer.parent;
      } else {
        var _this$_tree = this._tree;
        index = _this$_tree.index;
        parent = _this$_tree._parent;
      }
      for (; parent; _parent3 = parent, index = _parent3.index, parent = _parent3._parent, _parent3) {
        var _parent3;
        if (index > -1) for (var _i = index + dir, e = dir < 0 ? -1 : parent._tree.children.length; _i != e; _i += dir) {
          var child = parent._tree.children[_i];
          if (this.mode & IterMode.IncludeAnonymous || child instanceof TreeBuffer || !child.type.isAnonymous || hasChild(child)) return false;
        }
      }
      return true;
    }
  }, {
    key: "move",
    value: function move(dir, enter) {
      if (enter && this.enterChild(dir, 0, 4 /* Side.DontCare */)) return true;
      for (;;) {
        if (this.sibling(dir)) return true;
        if (this.atLastNode(dir) || !this.parent()) return false;
      }
    }
    /**
    Move to the next node in a
    [pre-order](https://en.wikipedia.org/wiki/Tree_traversal#Pre-order,_NLR)
    traversal, going from a node to its first child or, if the
    current node is empty or `enter` is false, its next sibling or
    the next sibling of the first parent node that has one.
    */
  }, {
    key: "next",
    value: function next() {
      var enter = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : true;
      return this.move(1, enter);
    }
    /**
    Move to the next node in a last-to-first pre-order traveral. A
    node is followed by its last child or, if it has none, its
    previous sibling or the previous sibling of the first parent
    node that has one.
    */
  }, {
    key: "prev",
    value: function prev() {
      var enter = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : true;
      return this.move(-1, enter);
    }
    /**
    Move the cursor to the innermost node that covers `pos`. If
    `side` is -1, it will enter nodes that end at `pos`. If it is 1,
    it will enter nodes that start at `pos`.
    */
  }, {
    key: "moveTo",
    value: function moveTo(pos) {
      var side = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
      // Move up to a node that actually holds the position, if possible
      while (this.from == this.to || (side < 1 ? this.from >= pos : this.from > pos) || (side > -1 ? this.to <= pos : this.to < pos)) if (!this.parent()) break;
      // Then scan down into child nodes as far as possible
      while (this.enterChild(1, pos, side)) {}
      return this;
    }
    /**
    Get a [syntax node](#common.SyntaxNode) at the cursor's current
    position.
    */
  }, {
    key: "node",
    get: function get() {
      if (!this.buffer) return this._tree;
      var cache = this.bufferNode,
        result = null,
        depth = 0;
      if (cache && cache.context == this.buffer) {
        scan: for (var index = this.index, d = this.stack.length; d >= 0;) {
          for (var c = cache; c; c = c._parent) if (c.index == index) {
            if (index == this.index) return c;
            result = c;
            depth = d + 1;
            break scan;
          }
          index = this.stack[--d];
        }
      }
      for (var i = depth; i < this.stack.length; i++) result = new BufferNode(this.buffer, result, this.stack[i]);
      return this.bufferNode = new BufferNode(this.buffer, result, this.index);
    }
    /**
    Get the [tree](#common.Tree) that represents the current node, if
    any. Will return null when the node is in a [tree
    buffer](#common.TreeBuffer).
    */
  }, {
    key: "tree",
    get: function get() {
      return this.buffer ? null : this._tree._tree;
    }
    /**
    Iterate over the current node and all its descendants, calling
    `enter` when entering a node and `leave`, if given, when leaving
    one. When `enter` returns `false`, any children of that node are
    skipped, and `leave` isn't called for it.
    */
  }, {
    key: "iterate",
    value: function iterate(enter, leave) {
      for (var depth = 0;;) {
        var mustLeave = false;
        if (this.type.isAnonymous || enter(this) !== false) {
          if (this.firstChild()) {
            depth++;
            continue;
          }
          if (!this.type.isAnonymous) mustLeave = true;
        }
        for (;;) {
          if (mustLeave && leave) leave(this);
          mustLeave = this.type.isAnonymous;
          if (this.nextSibling()) break;
          if (!depth) return;
          this.parent();
          depth--;
          mustLeave = true;
        }
      }
    }
    /**
    Test whether the current node matches a given context—a sequence
    of direct parent node names. Empty strings in the context array
    are treated as wildcards.
    */
  }, {
    key: "matchContext",
    value: function matchContext(context) {
      if (!this.buffer) return matchNodeContext(this.node, context);
      var buffer = this.buffer.buffer,
        types = buffer.set.types;
      for (var i = context.length - 1, d = this.stack.length - 1; i >= 0; d--) {
        if (d < 0) return matchNodeContext(this.node, context, i);
        var type = types[buffer.buffer[this.stack[d]]];
        if (!type.isAnonymous) {
          if (context[i] && context[i] != type.name) return false;
          i--;
        }
      }
      return true;
    }
  }]);
  return TreeCursor;
}();
function hasChild(tree) {
  return tree.children.some(function (ch) {
    return ch instanceof TreeBuffer || !ch.type.isAnonymous || hasChild(ch);
  });
}
function buildTree(data) {
  var _a;
  var buffer = data.buffer,
    nodeSet = data.nodeSet,
    _data$maxBufferLength = data.maxBufferLength,
    maxBufferLength = _data$maxBufferLength === void 0 ? DefaultBufferLength : _data$maxBufferLength,
    _data$reused = data.reused,
    reused = _data$reused === void 0 ? [] : _data$reused,
    _data$minRepeatType = data.minRepeatType,
    minRepeatType = _data$minRepeatType === void 0 ? nodeSet.types.length : _data$minRepeatType;
  var cursor = Array.isArray(buffer) ? new FlatBufferCursor(buffer, buffer.length) : buffer;
  var types = nodeSet.types;
  var contextHash = 0,
    lookAhead = 0;
  function takeNode(parentStart, minPos, children, positions, inRepeat) {
    var id = cursor.id,
      start = cursor.start,
      end = cursor.end,
      size = cursor.size;
    var lookAheadAtStart = lookAhead;
    while (size < 0) {
      cursor.next();
      if (size == -1 /* SpecialRecord.Reuse */) {
        var _node = reused[id];
        children.push(_node);
        positions.push(start - parentStart);
        return;
      } else if (size == -3 /* SpecialRecord.ContextChange */) {
        // Context change
        contextHash = id;
        return;
      } else if (size == -4 /* SpecialRecord.LookAhead */) {
        lookAhead = id;
        return;
      } else {
        throw new RangeError("Unrecognized record size: ".concat(size));
      }
    }
    var type = types[id],
      node,
      buffer;
    var startPos = start - parentStart;
    if (end - start <= maxBufferLength && (buffer = findBufferSize(cursor.pos - minPos, inRepeat))) {
      // Small enough for a buffer, and no reused nodes inside
      var _data = new Uint16Array(buffer.size - buffer.skip);
      var endPos = cursor.pos - buffer.size,
        index = _data.length;
      while (cursor.pos > endPos) index = copyToBuffer(buffer.start, _data, index);
      node = new TreeBuffer(_data, end - buffer.start, nodeSet);
      startPos = buffer.start - parentStart;
    } else {
      // Make it a node
      var _endPos = cursor.pos - size;
      cursor.next();
      var localChildren = [],
        localPositions = [];
      var localInRepeat = id >= minRepeatType ? id : -1;
      var lastGroup = 0,
        lastEnd = end;
      while (cursor.pos > _endPos) {
        if (localInRepeat >= 0 && cursor.id == localInRepeat && cursor.size >= 0) {
          if (cursor.end <= lastEnd - maxBufferLength) {
            makeRepeatLeaf(localChildren, localPositions, start, lastGroup, cursor.end, lastEnd, localInRepeat, lookAheadAtStart);
            lastGroup = localChildren.length;
            lastEnd = cursor.end;
          }
          cursor.next();
        } else {
          takeNode(start, _endPos, localChildren, localPositions, localInRepeat);
        }
      }
      if (localInRepeat >= 0 && lastGroup > 0 && lastGroup < localChildren.length) makeRepeatLeaf(localChildren, localPositions, start, lastGroup, start, lastEnd, localInRepeat, lookAheadAtStart);
      localChildren.reverse();
      localPositions.reverse();
      if (localInRepeat > -1 && lastGroup > 0) {
        var make = makeBalanced(type);
        node = balanceRange(type, localChildren, localPositions, 0, localChildren.length, 0, end - start, make, make);
      } else {
        node = makeTree(type, localChildren, localPositions, end - start, lookAheadAtStart - end);
      }
    }
    children.push(node);
    positions.push(startPos);
  }
  function makeBalanced(type) {
    return function (children, positions, length) {
      var lookAhead = 0,
        lastI = children.length - 1,
        last,
        lookAheadProp;
      if (lastI >= 0 && (last = children[lastI]) instanceof Tree) {
        if (!lastI && last.type == type && last.length == length) return last;
        if (lookAheadProp = last.prop(NodeProp.lookAhead)) lookAhead = positions[lastI] + last.length + lookAheadProp;
      }
      return makeTree(type, children, positions, length, lookAhead);
    };
  }
  function makeRepeatLeaf(children, positions, base, i, from, to, type, lookAhead) {
    var localChildren = [],
      localPositions = [];
    while (children.length > i) {
      localChildren.push(children.pop());
      localPositions.push(positions.pop() + base - from);
    }
    children.push(makeTree(nodeSet.types[type], localChildren, localPositions, to - from, lookAhead - to));
    positions.push(from - base);
  }
  function makeTree(type, children, positions, length) {
    var lookAhead = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : 0;
    var props = arguments.length > 5 ? arguments[5] : undefined;
    if (contextHash) {
      var pair = [NodeProp.contextHash, contextHash];
      props = props ? [pair].concat(props) : [pair];
    }
    if (lookAhead > 25) {
      var _pair = [NodeProp.lookAhead, lookAhead];
      props = props ? [_pair].concat(props) : [_pair];
    }
    return new Tree(type, children, positions, length, props);
  }
  function findBufferSize(maxSize, inRepeat) {
    // Scan through the buffer to find previous siblings that fit
    // together in a TreeBuffer, and don't contain any reused nodes
    // (which can't be stored in a buffer).
    // If `inRepeat` is > -1, ignore node boundaries of that type for
    // nesting, but make sure the end falls either at the start
    // (`maxSize`) or before such a node.
    var fork = cursor.fork();
    var size = 0,
      start = 0,
      skip = 0,
      minStart = fork.end - maxBufferLength;
    var result = {
      size: 0,
      start: 0,
      skip: 0
    };
    scan: for (var minPos = fork.pos - maxSize; fork.pos > minPos;) {
      var _nodeSize = fork.size;
      // Pretend nested repeat nodes of the same type don't exist
      if (fork.id == inRepeat && _nodeSize >= 0) {
        // Except that we store the current state as a valid return
        // value.
        result.size = size;
        result.start = start;
        result.skip = skip;
        skip += 4;
        size += 4;
        fork.next();
        continue;
      }
      var startPos = fork.pos - _nodeSize;
      if (_nodeSize < 0 || startPos < minPos || fork.start < minStart) break;
      var localSkipped = fork.id >= minRepeatType ? 4 : 0;
      var nodeStart = fork.start;
      fork.next();
      while (fork.pos > startPos) {
        if (fork.size < 0) {
          if (fork.size == -3 /* SpecialRecord.ContextChange */) localSkipped += 4;else break scan;
        } else if (fork.id >= minRepeatType) {
          localSkipped += 4;
        }
        fork.next();
      }
      start = nodeStart;
      size += _nodeSize;
      skip += localSkipped;
    }
    if (inRepeat < 0 || size == maxSize) {
      result.size = size;
      result.start = start;
      result.skip = skip;
    }
    return result.size > 4 ? result : undefined;
  }
  function copyToBuffer(bufferStart, buffer, index) {
    var id = cursor.id,
      start = cursor.start,
      end = cursor.end,
      size = cursor.size;
    cursor.next();
    if (size >= 0 && id < minRepeatType) {
      var startIndex = index;
      if (size > 4) {
        var endPos = cursor.pos - (size - 4);
        while (cursor.pos > endPos) index = copyToBuffer(bufferStart, buffer, index);
      }
      buffer[--index] = startIndex;
      buffer[--index] = end - bufferStart;
      buffer[--index] = start - bufferStart;
      buffer[--index] = id;
    } else if (size == -3 /* SpecialRecord.ContextChange */) {
      contextHash = id;
    } else if (size == -4 /* SpecialRecord.LookAhead */) {
      lookAhead = id;
    }
    return index;
  }
  var children = [],
    positions = [];
  while (cursor.pos > 0) takeNode(data.start || 0, data.bufferStart || 0, children, positions, -1);
  var length = (_a = data.length) !== null && _a !== void 0 ? _a : children.length ? positions[0] + children[0].length : 0;
  return new Tree(types[data.topID], children.reverse(), positions.reverse(), length);
}
var nodeSizeCache = new WeakMap();
function nodeSize(balanceType, node) {
  if (!balanceType.isAnonymous || node instanceof TreeBuffer || node.type != balanceType) return 1;
  var size = nodeSizeCache.get(node);
  if (size == null) {
    size = 1;
    var _iterator8 = _createForOfIteratorHelper(node.children),
      _step8;
    try {
      for (_iterator8.s(); !(_step8 = _iterator8.n()).done;) {
        var child = _step8.value;
        if (child.type != balanceType || !(child instanceof Tree)) {
          size = 1;
          break;
        }
        size += nodeSize(balanceType, child);
      }
    } catch (err) {
      _iterator8.e(err);
    } finally {
      _iterator8.f();
    }
    nodeSizeCache.set(node, size);
  }
  return size;
}
function balanceRange(
// The type the balanced tree's inner nodes.
balanceType,
// The direct children and their positions
children, positions,
// The index range in children/positions to use
from, to,
// The start position of the nodes, relative to their parent.
start,
// Length of the outer node
length,
// Function to build the top node of the balanced tree
mkTop,
// Function to build internal nodes for the balanced tree
mkTree) {
  var total = 0;
  for (var i = from; i < to; i++) total += nodeSize(balanceType, children[i]);
  var maxChild = Math.ceil(total * 1.5 / 8 /* Balance.BranchFactor */);
  var localChildren = [],
    localPositions = [];
  function divide(children, positions, from, to, offset) {
    for (var _i2 = from; _i2 < to;) {
      var groupFrom = _i2,
        groupStart = positions[_i2],
        groupSize = nodeSize(balanceType, children[_i2]);
      _i2++;
      for (; _i2 < to; _i2++) {
        var nextSize = nodeSize(balanceType, children[_i2]);
        if (groupSize + nextSize >= maxChild) break;
        groupSize += nextSize;
      }
      if (_i2 == groupFrom + 1) {
        if (groupSize > maxChild) {
          var only = children[groupFrom]; // Only trees can have a size > 1
          divide(only.children, only.positions, 0, only.children.length, positions[groupFrom] + offset);
          continue;
        }
        localChildren.push(children[groupFrom]);
      } else {
        var _length = positions[_i2 - 1] + children[_i2 - 1].length - groupStart;
        localChildren.push(balanceRange(balanceType, children, positions, groupFrom, _i2, groupStart, _length, null, mkTree));
      }
      localPositions.push(groupStart + offset - start);
    }
  }
  divide(children, positions, from, to, 0);
  return (mkTop || mkTree)(localChildren, localPositions, length);
}
/**
Provides a way to associate values with pieces of trees. As long
as that part of the tree is reused, the associated values can be
retrieved from an updated tree.
*/
var NodeWeakMap = /*#__PURE__*/function () {
  function NodeWeakMap() {
    _classCallCheck(this, NodeWeakMap);
    this.map = new WeakMap();
  }
  _createClass(NodeWeakMap, [{
    key: "setBuffer",
    value: function setBuffer(buffer, index, value) {
      var inner = this.map.get(buffer);
      if (!inner) this.map.set(buffer, inner = new Map());
      inner.set(index, value);
    }
  }, {
    key: "getBuffer",
    value: function getBuffer(buffer, index) {
      var inner = this.map.get(buffer);
      return inner && inner.get(index);
    }
    /**
    Set the value for this syntax node.
    */
  }, {
    key: "set",
    value: function set(node, value) {
      if (node instanceof BufferNode) this.setBuffer(node.context.buffer, node.index, value);else if (node instanceof TreeNode) this.map.set(node.tree, value);
    }
    /**
    Retrieve value for this syntax node, if it exists in the map.
    */
  }, {
    key: "get",
    value: function get(node) {
      return node instanceof BufferNode ? this.getBuffer(node.context.buffer, node.index) : node instanceof TreeNode ? this.map.get(node.tree) : undefined;
    }
    /**
    Set the value for the node that a cursor currently points to.
    */
  }, {
    key: "cursorSet",
    value: function cursorSet(cursor, value) {
      if (cursor.buffer) this.setBuffer(cursor.buffer.buffer, cursor.index, value);else this.map.set(cursor.tree, value);
    }
    /**
    Retrieve the value for the node that a cursor currently points
    to.
    */
  }, {
    key: "cursorGet",
    value: function cursorGet(cursor) {
      return cursor.buffer ? this.getBuffer(cursor.buffer.buffer, cursor.index) : this.map.get(cursor.tree);
    }
  }]);
  return NodeWeakMap;
}();
/**
Tree fragments are used during [incremental
parsing](#common.Parser.startParse) to track parts of old trees
that can be reused in a new parse. An array of fragments is used
to track regions of an old tree whose nodes might be reused in new
parses. Use the static
[`applyChanges`](#common.TreeFragment^applyChanges) method to
update fragments for document changes.
*/
var TreeFragment = /*#__PURE__*/function () {
  /**
  Construct a tree fragment. You'll usually want to use
  [`addTree`](#common.TreeFragment^addTree) and
  [`applyChanges`](#common.TreeFragment^applyChanges) instead of
  calling this directly.
  */
  function TreeFragment(
  /**
  The start of the unchanged range pointed to by this fragment.
  This refers to an offset in the _updated_ document (as opposed
  to the original tree).
  */
  from,
  /**
  The end of the unchanged range.
  */
  to,
  /**
  The tree that this fragment is based on.
  */
  tree,
  /**
  The offset between the fragment's tree and the document that
  this fragment can be used against. Add this when going from
  document to tree positions, subtract it to go from tree to
  document positions.
  */
  offset) {
    var openStart = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : false;
    var openEnd = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : false;
    _classCallCheck(this, TreeFragment);
    this.from = from;
    this.to = to;
    this.tree = tree;
    this.offset = offset;
    this.open = (openStart ? 1 /* Open.Start */ : 0) | (openEnd ? 2 /* Open.End */ : 0);
  }
  /**
  Whether the start of the fragment represents the start of a
  parse, or the end of a change. (In the second case, it may not
  be safe to reuse some nodes at the start, depending on the
  parsing algorithm.)
  */
  _createClass(TreeFragment, [{
    key: "openStart",
    get: function get() {
      return (this.open & 1 /* Open.Start */) > 0;
    }
    /**
    Whether the end of the fragment represents the end of a
    full-document parse, or the start of a change.
    */
  }, {
    key: "openEnd",
    get: function get() {
      return (this.open & 2 /* Open.End */) > 0;
    }
    /**
    Create a set of fragments from a freshly parsed tree, or update
    an existing set of fragments by replacing the ones that overlap
    with a tree with content from the new tree. When `partial` is
    true, the parse is treated as incomplete, and the resulting
    fragment has [`openEnd`](#common.TreeFragment.openEnd) set to
    true.
    */
  }], [{
    key: "addTree",
    value: function addTree(tree) {
      var fragments = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
      var partial = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
      var result = [new TreeFragment(0, tree.length, tree, 0, false, partial)];
      var _iterator9 = _createForOfIteratorHelper(fragments),
        _step9;
      try {
        for (_iterator9.s(); !(_step9 = _iterator9.n()).done;) {
          var f = _step9.value;
          if (f.to > tree.length) result.push(f);
        }
      } catch (err) {
        _iterator9.e(err);
      } finally {
        _iterator9.f();
      }
      return result;
    }
    /**
    Apply a set of edits to an array of fragments, removing or
    splitting fragments as necessary to remove edited ranges, and
    adjusting offsets for fragments that moved.
    */
  }, {
    key: "applyChanges",
    value: function applyChanges(fragments, changes) {
      var minGap = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 128;
      if (!changes.length) return fragments;
      var result = [];
      var fI = 1,
        nextF = fragments.length ? fragments[0] : null;
      for (var cI = 0, pos = 0, off = 0;; cI++) {
        var nextC = cI < changes.length ? changes[cI] : null;
        var nextPos = nextC ? nextC.fromA : 1e9;
        if (nextPos - pos >= minGap) while (nextF && nextF.from < nextPos) {
          var cut = nextF;
          if (pos >= cut.from || nextPos <= cut.to || off) {
            var fFrom = Math.max(cut.from, pos) - off,
              fTo = Math.min(cut.to, nextPos) - off;
            cut = fFrom >= fTo ? null : new TreeFragment(fFrom, fTo, cut.tree, cut.offset + off, cI > 0, !!nextC);
          }
          if (cut) result.push(cut);
          if (nextF.to > nextPos) break;
          nextF = fI < fragments.length ? fragments[fI++] : null;
        }
        if (!nextC) break;
        pos = nextC.toA;
        off = nextC.toA - nextC.toB;
      }
      return result;
    }
  }]);
  return TreeFragment;
}();
/**
A superclass that parsers should extend.
*/
var Parser = /*#__PURE__*/function () {
  function Parser() {
    _classCallCheck(this, Parser);
  }
  _createClass(Parser, [{
    key: "startParse",
    value:
    /**
    Start a parse, returning a [partial parse](#common.PartialParse)
    object. [`fragments`](#common.TreeFragment) can be passed in to
    make the parse incremental.
    
    By default, the entire input is parsed. You can pass `ranges`,
    which should be a sorted array of non-empty, non-overlapping
    ranges, to parse only those ranges. The tree returned in that
    case will start at `ranges[0].from`.
    */
    function startParse(input, fragments, ranges) {
      if (typeof input == "string") input = new StringInput(input);
      ranges = !ranges ? [new Range(0, input.length)] : ranges.length ? ranges.map(function (r) {
        return new Range(r.from, r.to);
      }) : [new Range(0, 0)];
      return this.createParse(input, fragments || [], ranges);
    }
    /**
    Run a full parse, returning the resulting tree.
    */
  }, {
    key: "parse",
    value: function parse(input, fragments, ranges) {
      var parse = this.startParse(input, fragments, ranges);
      for (;;) {
        var done = parse.advance();
        if (done) return done;
      }
    }
  }]);
  return Parser;
}();
var StringInput = /*#__PURE__*/function () {
  function StringInput(string) {
    _classCallCheck(this, StringInput);
    this.string = string;
  }
  _createClass(StringInput, [{
    key: "length",
    get: function get() {
      return this.string.length;
    }
  }, {
    key: "chunk",
    value: function chunk(from) {
      return this.string.slice(from);
    }
  }, {
    key: "lineChunks",
    get: function get() {
      return false;
    }
  }, {
    key: "read",
    value: function read(from, to) {
      return this.string.slice(from, to);
    }
  }]);
  return StringInput;
}();
/**
Create a parse wrapper that, after the inner parse completes,
scans its tree for mixed language regions with the `nest`
function, runs the resulting [inner parses](#common.NestedParse),
and then [mounts](#common.NodeProp^mounted) their results onto the
tree.
*/
function parseMixed(nest) {
  return function (parse, input, fragments, ranges) {
    return new MixedParse(parse, nest, input, fragments, ranges);
  };
}
var InnerParse = /*#__PURE__*/_createClass(function InnerParse(parser, parse, overlay, target, ranges) {
  _classCallCheck(this, InnerParse);
  this.parser = parser;
  this.parse = parse;
  this.overlay = overlay;
  this.target = target;
  this.ranges = ranges;
  if (!ranges.length || ranges.some(function (r) {
    return r.from >= r.to;
  })) throw new RangeError("Invalid inner parse ranges given: " + JSON.stringify(ranges));
});
var ActiveOverlay = /*#__PURE__*/_createClass(function ActiveOverlay(parser, predicate, mounts, index, start, target, prev) {
  _classCallCheck(this, ActiveOverlay);
  this.parser = parser;
  this.predicate = predicate;
  this.mounts = mounts;
  this.index = index;
  this.start = start;
  this.target = target;
  this.prev = prev;
  this.depth = 0;
  this.ranges = [];
});
var stoppedInner = new NodeProp({
  perNode: true
});
var MixedParse = /*#__PURE__*/function () {
  function MixedParse(base, nest, input, fragments, ranges) {
    _classCallCheck(this, MixedParse);
    this.nest = nest;
    this.input = input;
    this.fragments = fragments;
    this.ranges = ranges;
    this.inner = [];
    this.innerDone = 0;
    this.baseTree = null;
    this.stoppedAt = null;
    this.baseParse = base;
  }
  _createClass(MixedParse, [{
    key: "advance",
    value: function advance() {
      if (this.baseParse) {
        var _done = this.baseParse.advance();
        if (!_done) return null;
        this.baseParse = null;
        this.baseTree = _done;
        this.startInner();
        if (this.stoppedAt != null) {
          var _iterator10 = _createForOfIteratorHelper(this.inner),
            _step10;
          try {
            for (_iterator10.s(); !(_step10 = _iterator10.n()).done;) {
              var _inner = _step10.value;
              _inner.parse.stopAt(this.stoppedAt);
            }
          } catch (err) {
            _iterator10.e(err);
          } finally {
            _iterator10.f();
          }
        }
      }
      if (this.innerDone == this.inner.length) {
        var result = this.baseTree;
        if (this.stoppedAt != null) result = new Tree(result.type, result.children, result.positions, result.length, result.propValues.concat([[stoppedInner, this.stoppedAt]]));
        return result;
      }
      var inner = this.inner[this.innerDone],
        done = inner.parse.advance();
      if (done) {
        this.innerDone++;
        // This is a somewhat dodgy but super helpful hack where we
        // patch up nodes created by the inner parse (and thus
        // presumably not aliased anywhere else) to hold the information
        // about the inner parse.
        var props = Object.assign(Object.create(null), inner.target.props);
        props[NodeProp.mounted.id] = new MountedTree(done, inner.overlay, inner.parser);
        inner.target.props = props;
      }
      return null;
    }
  }, {
    key: "parsedPos",
    get: function get() {
      if (this.baseParse) return 0;
      var pos = this.input.length;
      for (var i = this.innerDone; i < this.inner.length; i++) {
        if (this.inner[i].ranges[0].from < pos) pos = Math.min(pos, this.inner[i].parse.parsedPos);
      }
      return pos;
    }
  }, {
    key: "stopAt",
    value: function stopAt(pos) {
      this.stoppedAt = pos;
      if (this.baseParse) this.baseParse.stopAt(pos);else for (var i = this.innerDone; i < this.inner.length; i++) this.inner[i].parse.stopAt(pos);
    }
  }, {
    key: "startInner",
    value: function startInner() {
      var fragmentCursor = new FragmentCursor(this.fragments);
      var overlay = null;
      var covered = null;
      var cursor = new TreeCursor(new TreeNode(this.baseTree, this.ranges[0].from, 0, null), IterMode.IncludeAnonymous | IterMode.IgnoreMounts);
      scan: for (var nest, isCovered; this.stoppedAt == null || cursor.from < this.stoppedAt;) {
        var enter = true,
          range = void 0;
        if (fragmentCursor.hasNode(cursor)) {
          if (overlay) {
            var match = overlay.mounts.find(function (m) {
              return m.frag.from <= cursor.from && m.frag.to >= cursor.to && m.mount.overlay;
            });
            if (match) {
              var _iterator11 = _createForOfIteratorHelper(match.mount.overlay),
                _step11;
              try {
                var _loop = function _loop() {
                  var r = _step11.value;
                  var from = r.from + match.pos,
                    to = r.to + match.pos;
                  if (from >= cursor.from && to <= cursor.to && !overlay.ranges.some(function (r) {
                    return r.from < to && r.to > from;
                  })) overlay.ranges.push({
                    from: from,
                    to: to
                  });
                };
                for (_iterator11.s(); !(_step11 = _iterator11.n()).done;) {
                  _loop();
                }
              } catch (err) {
                _iterator11.e(err);
              } finally {
                _iterator11.f();
              }
            }
          }
          enter = false;
        } else if (covered && (isCovered = checkCover(covered.ranges, cursor.from, cursor.to))) {
          enter = isCovered != 2 /* Cover.Full */;
        } else if (!cursor.type.isAnonymous && cursor.from < cursor.to && (nest = this.nest(cursor, this.input))) {
          if (!cursor.tree) materialize(cursor);
          var oldMounts = fragmentCursor.findMounts(cursor.from, nest.parser);
          if (typeof nest.overlay == "function") {
            overlay = new ActiveOverlay(nest.parser, nest.overlay, oldMounts, this.inner.length, cursor.from, cursor.tree, overlay);
          } else {
            var ranges = punchRanges(this.ranges, nest.overlay || [new Range(cursor.from, cursor.to)]);
            if (ranges.length) this.inner.push(new InnerParse(nest.parser, nest.parser.startParse(this.input, enterFragments(oldMounts, ranges), ranges), nest.overlay ? nest.overlay.map(function (r) {
              return new Range(r.from - cursor.from, r.to - cursor.from);
            }) : null, cursor.tree, ranges));
            if (!nest.overlay) enter = false;else if (ranges.length) covered = {
              ranges: ranges,
              depth: 0,
              prev: covered
            };
          }
        } else if (overlay && (range = overlay.predicate(cursor))) {
          if (range === true) range = new Range(cursor.from, cursor.to);
          if (range.from < range.to) overlay.ranges.push(range);
        }
        if (enter && cursor.firstChild()) {
          if (overlay) overlay.depth++;
          if (covered) covered.depth++;
        } else {
          for (;;) {
            if (cursor.nextSibling()) break;
            if (!cursor.parent()) break scan;
            if (overlay && ! --overlay.depth) {
              var _ranges = punchRanges(this.ranges, overlay.ranges);
              if (_ranges.length) this.inner.splice(overlay.index, 0, new InnerParse(overlay.parser, overlay.parser.startParse(this.input, enterFragments(overlay.mounts, _ranges), _ranges), overlay.ranges.map(function (r) {
                return new Range(r.from - overlay.start, r.to - overlay.start);
              }), overlay.target, _ranges));
              overlay = overlay.prev;
            }
            if (covered && ! --covered.depth) covered = covered.prev;
          }
        }
      }
    }
  }]);
  return MixedParse;
}();
function checkCover(covered, from, to) {
  var _iterator12 = _createForOfIteratorHelper(covered),
    _step12;
  try {
    for (_iterator12.s(); !(_step12 = _iterator12.n()).done;) {
      var range = _step12.value;
      if (range.from >= to) break;
      if (range.to > from) return range.from <= from && range.to >= to ? 2 /* Cover.Full */ : 1 /* Cover.Partial */;
    }
  } catch (err) {
    _iterator12.e(err);
  } finally {
    _iterator12.f();
  }
  return 0 /* Cover.None */;
}
// Take a piece of buffer and convert it into a stand-alone
// TreeBuffer.
function sliceBuf(buf, startI, endI, nodes, positions, off) {
  if (startI < endI) {
    var from = buf.buffer[startI + 1];
    nodes.push(buf.slice(startI, endI, from));
    positions.push(from - off);
  }
}
// This function takes a node that's in a buffer, and converts it, and
// its parent buffer nodes, into a Tree. This is again acting on the
// assumption that the trees and buffers have been constructed by the
// parse that was ran via the mix parser, and thus aren't shared with
// any other code, making violations of the immutability safe.
function materialize(cursor) {
  var node = cursor.node,
    depth = 0;
  // Scan up to the nearest tree
  do {
    cursor.parent();
    depth++;
  } while (!cursor.tree);
  // Find the index of the buffer in that tree
  var i = 0,
    base = cursor.tree,
    off = 0;
  for (;; i++) {
    off = base.positions[i] + cursor.from;
    if (off <= node.from && off + base.children[i].length >= node.to) break;
  }
  var buf = base.children[i],
    b = buf.buffer;
  // Split a level in the buffer, putting the nodes before and after
  // the child that contains `node` into new buffers.
  function split(startI, endI, type, innerOffset, length) {
    var i = startI;
    while (b[i + 2] + off <= node.from) i = b[i + 3];
    var children = [],
      positions = [];
    sliceBuf(buf, startI, i, children, positions, innerOffset);
    var from = b[i + 1],
      to = b[i + 2];
    var isTarget = from + off == node.from && to + off == node.to && b[i] == node.type.id;
    children.push(isTarget ? node.toTree() : split(i + 4, b[i + 3], buf.set.types[b[i]], from, to - from));
    positions.push(from - innerOffset);
    sliceBuf(buf, b[i + 3], endI, children, positions, innerOffset);
    return new Tree(type, children, positions, length);
  }
  base.children[i] = split(0, b.length, NodeType.none, 0, buf.length);
  // Move the cursor back to the target node
  for (var d = 0; d <= depth; d++) cursor.childAfter(node.from);
}
var StructureCursor = /*#__PURE__*/function () {
  function StructureCursor(root, offset) {
    _classCallCheck(this, StructureCursor);
    this.offset = offset;
    this.done = false;
    this.cursor = root.cursor(IterMode.IncludeAnonymous | IterMode.IgnoreMounts);
  }
  // Move to the first node (in pre-order) that starts at or after `pos`.
  _createClass(StructureCursor, [{
    key: "moveTo",
    value: function moveTo(pos) {
      var cursor = this.cursor,
        p = pos - this.offset;
      while (!this.done && cursor.from < p) {
        if (cursor.to >= pos && cursor.enter(p, 1, IterMode.IgnoreOverlays | IterMode.ExcludeBuffers)) ;else if (!cursor.next(false)) this.done = true;
      }
    }
  }, {
    key: "hasNode",
    value: function hasNode(cursor) {
      this.moveTo(cursor.from);
      if (!this.done && this.cursor.from + this.offset == cursor.from && this.cursor.tree) {
        for (var tree = this.cursor.tree;;) {
          if (tree == cursor.tree) return true;
          if (tree.children.length && tree.positions[0] == 0 && tree.children[0] instanceof Tree) tree = tree.children[0];else break;
        }
      }
      return false;
    }
  }]);
  return StructureCursor;
}();
var FragmentCursor = /*#__PURE__*/function () {
  function FragmentCursor(fragments) {
    _classCallCheck(this, FragmentCursor);
    var _a;
    this.fragments = fragments;
    this.curTo = 0;
    this.fragI = 0;
    if (fragments.length) {
      var first = this.curFrag = fragments[0];
      this.curTo = (_a = first.tree.prop(stoppedInner)) !== null && _a !== void 0 ? _a : first.to;
      this.inner = new StructureCursor(first.tree, -first.offset);
    } else {
      this.curFrag = this.inner = null;
    }
  }
  _createClass(FragmentCursor, [{
    key: "hasNode",
    value: function hasNode(node) {
      while (this.curFrag && node.from >= this.curTo) this.nextFrag();
      return this.curFrag && this.curFrag.from <= node.from && this.curTo >= node.to && this.inner.hasNode(node);
    }
  }, {
    key: "nextFrag",
    value: function nextFrag() {
      var _a;
      this.fragI++;
      if (this.fragI == this.fragments.length) {
        this.curFrag = this.inner = null;
      } else {
        var frag = this.curFrag = this.fragments[this.fragI];
        this.curTo = (_a = frag.tree.prop(stoppedInner)) !== null && _a !== void 0 ? _a : frag.to;
        this.inner = new StructureCursor(frag.tree, -frag.offset);
      }
    }
  }, {
    key: "findMounts",
    value: function findMounts(pos, parser) {
      var _a;
      var result = [];
      if (this.inner) {
        this.inner.cursor.moveTo(pos, 1);
        for (var _pos = this.inner.cursor.node; _pos; _pos = _pos.parent) {
          var mount = (_a = _pos.tree) === null || _a === void 0 ? void 0 : _a.prop(NodeProp.mounted);
          if (mount && mount.parser == parser) {
            for (var i = this.fragI; i < this.fragments.length; i++) {
              var frag = this.fragments[i];
              if (frag.from >= _pos.to) break;
              if (frag.tree == this.curFrag.tree) result.push({
                frag: frag,
                pos: _pos.from - frag.offset,
                mount: mount
              });
            }
          }
        }
      }
      return result;
    }
  }]);
  return FragmentCursor;
}();
function punchRanges(outer, ranges) {
  var copy = null,
    current = ranges;
  for (var i = 1, j = 0; i < outer.length; i++) {
    var gapFrom = outer[i - 1].to,
      gapTo = outer[i].from;
    for (; j < current.length; j++) {
      var r = current[j];
      if (r.from >= gapTo) break;
      if (r.to <= gapFrom) continue;
      if (!copy) current = copy = ranges.slice();
      if (r.from < gapFrom) {
        copy[j] = new Range(r.from, gapFrom);
        if (r.to > gapTo) copy.splice(j + 1, 0, new Range(gapTo, r.to));
      } else if (r.to > gapTo) {
        copy[j--] = new Range(gapTo, r.to);
      } else {
        copy.splice(j--, 1);
      }
    }
  }
  return current;
}
function findCoverChanges(a, b, from, to) {
  var iA = 0,
    iB = 0,
    inA = false,
    inB = false,
    pos = -1e9;
  var result = [];
  for (;;) {
    var nextA = iA == a.length ? 1e9 : inA ? a[iA].to : a[iA].from;
    var nextB = iB == b.length ? 1e9 : inB ? b[iB].to : b[iB].from;
    if (inA != inB) {
      var start = Math.max(pos, from),
        end = Math.min(nextA, nextB, to);
      if (start < end) result.push(new Range(start, end));
    }
    pos = Math.min(nextA, nextB);
    if (pos == 1e9) break;
    if (nextA == pos) {
      if (!inA) inA = true;else {
        inA = false;
        iA++;
      }
    }
    if (nextB == pos) {
      if (!inB) inB = true;else {
        inB = false;
        iB++;
      }
    }
  }
  return result;
}
// Given a number of fragments for the outer tree, and a set of ranges
// to parse, find fragments for inner trees mounted around those
// ranges, if any.
function enterFragments(mounts, ranges) {
  var result = [];
  var _iterator13 = _createForOfIteratorHelper(mounts),
    _step13;
  try {
    var _loop2 = function _loop2() {
      var _step13$value = _step13.value,
        pos = _step13$value.pos,
        mount = _step13$value.mount,
        frag = _step13$value.frag;
      var startPos = pos + (mount.overlay ? mount.overlay[0].from : 0),
        endPos = startPos + mount.tree.length;
      var from = Math.max(frag.from, startPos),
        to = Math.min(frag.to, endPos);
      if (mount.overlay) {
        var overlay = mount.overlay.map(function (r) {
          return new Range(r.from + pos, r.to + pos);
        });
        var changes = findCoverChanges(ranges, overlay, from, to);
        for (var i = 0, _pos2 = from;; i++) {
          var last = i == changes.length,
            end = last ? to : changes[i].from;
          if (end > _pos2) result.push(new TreeFragment(_pos2, end, mount.tree, -startPos, frag.from >= _pos2 || frag.openStart, frag.to <= end || frag.openEnd));
          if (last) break;
          _pos2 = changes[i].to;
        }
      } else {
        result.push(new TreeFragment(from, to, mount.tree, -startPos, frag.from >= startPos || frag.openStart, frag.to <= endPos || frag.openEnd));
      }
    };
    for (_iterator13.s(); !(_step13 = _iterator13.n()).done;) {
      _loop2();
    }
  } catch (err) {
    _iterator13.e(err);
  } finally {
    _iterator13.f();
  }
  return result;
}
export { DefaultBufferLength, IterMode, MountedTree, NodeProp, NodeSet, NodeType, NodeWeakMap, Parser, Tree, TreeBuffer, TreeCursor, TreeFragment, parseMixed };